/** @jsx jsx */
import React, { useState } from 'react';
import { jsx } from '@emotion/core';
import { FormHeader } from '../basic/FormHeader';
import { ActionButton } from '../basic/ActionButton';
import { reactivateUser } from '../../api';
import { useTrackLoadUnload, trackingClient } from '../../ext/tracking';
import { Marker } from '../meta/Marker';
import {
  SHEERID_DOCUPLOAD_STARTED,
  SHEERID_AUTOMATIC_SUCCESS,
  CENGAGE_VERIFICATION_START,
  SHEERID_AUTOMATIC_SUCCESS_MESSAGE,
  CENGAGE_VERIFICATION_START_MESSAGE,
  SHEERID_DOCUPLOAD_STARTED_MESSAGE
} from '../screens/Profile/VerificationResponses';
import { Button } from '../magma-wrap/Button';
import { ButtonColor } from 'react-magma-dom';
import { DataInput } from '../screens/Profile/data-input/DataInput';
import { isValidStandardInput, ValidatorResponse } from '../data-inputs/validated/Validator';
import { Link } from '../basic/Links';

export const TEST_ID = {
  MARKER: 'marker-faculty-email-sent',
  BUTTON_DOC_UPLOAD: 'button-doc-upload',
  VERIFICATION_RESPONSE: 'text-verification-response',
  LOGIN_BUTTON: 'button-login',
  ACTIVATE_BUTTON: 'button-activate',
  ACTIVATE_INPUT: 'input-activate'
};

export const VERIFY_BUTTON = 'Verify My Identity';

export interface FacultyEmailSentProps {
  emailAddress: string;
  verifyId?: string;
  verifyStatus?: string;
}

export const FacultyEmailSent: React.FunctionComponent<FacultyEmailSentProps> = props => {
  const category = trackingClient.event.facultyRegistrationActivationSent;
  useTrackLoadUnload(category);
  const basepath = `${window?.backend?.sheerIdDocUploadUrl}/?verificationId=${props.verifyId}`;

  const getEmailResult = () => {
    if (props.verifyId && props.verifyStatus === SHEERID_DOCUPLOAD_STARTED) {
      return (
        <React.Fragment>{SHEERID_DOCUPLOAD_STARTED_MESSAGE}</React.Fragment>
      );
    } else if (props.verifyStatus === SHEERID_AUTOMATIC_SUCCESS) {
      return SHEERID_AUTOMATIC_SUCCESS_MESSAGE;
    } else if (props.verifyStatus === CENGAGE_VERIFICATION_START) {
      return CENGAGE_VERIFICATION_START_MESSAGE;
    }
  };
  const [isValidToken, setIsValidToken] = useState(false);
  const [tokenValue, setTokenValue] = useState('');

  return (
    <React.Fragment>
      <Marker testId={TEST_ID.MARKER} />

      <FormHeader>Email sent!</FormHeader>
      <p data-testid={TEST_ID.VERIFICATION_RESPONSE}>{getEmailResult()}</p>
      <p data-testid="email-instructions">
        A confirmation email has been sent to{' '}
        <span style={{ wordWrap: 'break-word' }}>
          {props.emailAddress}
        </span>
        . Use the account activation link or enter the activation code included in the email.
      </p>
      <p>If you need help activating your account, contact{' '}
        <Link href={window?.backend?.cengagePhoneHelpUrl}>Support Services</Link>.
      </p>
      {props.verifyStatus === SHEERID_DOCUPLOAD_STARTED && (
        <Button
          block
          testId={TEST_ID.BUTTON_DOC_UPLOAD}
          value={VERIFY_BUTTON}
          onClick={() => {
            window.open(basepath, '_blank');
          }}
        ></Button>
      )}
      <DataInput
        validator={async (value: string) => {
          const isValid: ValidatorResponse = isValidStandardInput(value);
          if (!isValid.response) {
            setIsValidToken(false);
            throw new Error(isValid.reason);
          } else {
            setTokenValue(value);
            setIsValidToken(true);
          }
        }}
        dataLabel={'Activation code'}
        id={'recoveryToken'}
        testId={TEST_ID.ACTIVATE_INPUT}
      />
      <Button
        onClick={() => {
          (document.location as any) = '/welcome/' + tokenValue +
              '?email=' + props.emailAddress;
        }}
        value="SUBMIT"
        block
        testId={TEST_ID.ACTIVATE_BUTTON}
        disabled={!isValidToken}
      />
      <hr/>
      <ActionButton
        block
        onAction={() => {
          category.resendEmailClick();
          return reactivateUser(props.emailAddress);
        }}
        onActionChildren="Sending..."
        toastOnFailure="Email sending error"
        toastOnSuccess="Email sent!"
        testId="resend-email"
        color={ButtonColor.secondary}
      >
        RESEND ACCOUNT ACTIVATION EMAIL
      </ActionButton>
      <Button
        onClick={() => {
          (document.location as any) = '/login' + document.location.search;
        }}
        value="BACK TO SIGN IN"
        block
        testId={TEST_ID.LOGIN_BUTTON}
        color={
          props.verifyStatus === SHEERID_DOCUPLOAD_STARTED
            ? ButtonColor.secondary
            : ButtonColor.subtle
        }
      />
    </React.Fragment>
  );
};